import React, { useEffect, useRef, useState } from 'react';
import Icon from 'components/AppIcon';
import { useToast } from 'components/ui/Toast';

/** Sprint 15 QA — columns come from the org's pipeline config (custom stages). */
const STAGE_COLORS = [
  { color: '#3B82F6', bg: '#DBEAFE', headerBg: '#1D4ED8' },
  { color: '#D97706', bg: '#FEF3C7', headerBg: '#B45309' },
  { color: '#7C3AED', bg: '#EDE9FE', headerBg: '#6D28D9' },
  { color: '#0891B2', bg: '#CFFAFE', headerBg: '#0E7490' },
  { color: '#15803D', bg: '#DCFCE7', headerBg: '#166534' },
  { color: '#B91C1C', bg: '#FEE2E2', headerBg: '#991B1B' },
];

const DEFAULT_STAGES = [
  { id: 1, name: 'Prospecting', isWon: false, isClosed: false },
  { id: 2, name: 'Qualification', isWon: false, isClosed: false },
  { id: 3, name: 'Proposal', isWon: false, isClosed: false },
  { id: 4, name: 'Negotiation', isWon: false, isClosed: false },
  { id: 5, name: 'Closed Won', isWon: true, isClosed: true },
  { id: 6, name: 'Closed Lost', isWon: false, isClosed: true },
];

function mapBoardToDeals(board) {
  const rows = [];
  Object.entries(board || {})?.forEach(([stage, cards]) => {
    (cards || [])?.forEach((card, index) => {
      rows.push({
        id: card?.id,
        name: card?.name,
        company: card?.accountName,
        value: Number(card?.amount || 0),
        probability: Number(card?.probabilityPercent || 0),
        closeDate: card?.expectedCloseDateUtc ? new Date(card.expectedCloseDateUtc)?.toISOString()?.slice(0, 10) : '',
        rep: card?.ownerName,
        notes: card?.description || '',
        stage: String(card?.stage ?? stage),
        order: index,
      });
    });
  });
  return rows;
}

function formatRupees(value) {
  if (value >= 10000000) return `₹${(value / 10000000)?.toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000)?.toFixed(1)}L`;
  return `₹${value?.toLocaleString('en-IN')}`;
}

export default function PipelineKanban({ filterRep, board, stages, onMoveStage, onEditDeal, onDeleteDeal }) {
  const toast = useToast();
  const [deals, setDeals] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const dealsRef = useRef(deals);

  useEffect(() => {
    dealsRef.current = deals;
  }, [deals]);

  const columns = (Array.isArray(stages) && stages.length ? stages : DEFAULT_STAGES).map((s, i) => ({
    ...s,
    ...(STAGE_COLORS[i % STAGE_COLORS.length] || STAGE_COLORS[0]),
  }));

  useEffect(() => {
    const mapped = mapBoardToDeals(board?.stages || board);
    setDeals(mapped);
  }, [board]);

  const handleDragStart = (e, dealId) => {
    setDraggingId(dealId);
    e?.dataTransfer?.setData('dealId', String(dealId));
  };

  const handleDragOver = (e, stageId) => {
    e?.preventDefault();
    setDragOverStage(stageId);
  };

  const handleDrop = async (e, stageId) => {
    e?.preventDefault();
    const dealId = e?.dataTransfer?.getData('dealId');
    if (!dealId) return;
    const previous = dealsRef.current;
    setDeals((prev) => prev?.map((d) => String(d?.id) === String(dealId) ? { ...d, stage: String(stageId) } : d));

    if (onMoveStage) {
      try {
        await onMoveStage(dealId, Number(stageId));
      } catch (error) {
        // Roll back the optimistic move so the board never shows a lie.
        setDeals(previous);
        toast.error(error?.message || 'Could not move the deal.');
      }
    }

    setDraggingId(null);
    setDragOverStage(null);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };

  const filteredDeals = filterRep && filterRep !== 'all'
    ? deals?.filter((d) => (filterRep === 'Unassigned' ? !d?.rep : d?.rep === filterRep))
    : deals;

  return (
    <div className="overflow-x-auto pb-4" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="flex gap-3" style={{ minWidth: '1320px', minHeight: '500px' }}>
        {columns?.map((stage) => {
          const stageDeals = filteredDeals?.filter((d) => d?.stage === String(stage?.id));
          const totalValue = stageDeals?.reduce((sum, d) => sum + d?.value, 0);
          const avgDeal = stageDeals?.length ? totalValue / stageDeals?.length : 0;
          const isDragOver = dragOverStage === String(stage?.id);

          return (
            <div
              key={stage?.id}
              className="flex flex-col rounded-xl flex-shrink-0 transition-all duration-150"
              style={{
                width: '220px',
                minWidth: '220px',
                background: isDragOver ? stage?.bg : 'var(--color-card)',
                border: `1px solid ${isDragOver ? stage?.color : 'var(--color-border)'}`,
                boxShadow: isDragOver ? `0 0 0 2px ${stage?.color}40` : 'none',
              }}
              onDragOver={(e) => handleDragOver(e, stage?.id)}
              onDrop={(e) => handleDrop(e, stage?.id)}
            >
              {/* Stage header */}
              <div
                className="px-3 py-2.5 rounded-t-xl"
                style={{ background: stage?.headerBg }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-caption font-semibold text-xs text-white">{stage?.name}</span>
                  <span
                    className="font-caption text-xs font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.25)', color: '#fff' }}
                  >
                    {stageDeals?.length}
                  </span>
                </div>
                <div className="font-caption text-xs text-white/80">{formatRupees(totalValue)}</div>
                {stageDeals?.length > 0 && (
                  <div className="font-caption text-xs text-white/60">avg {formatRupees(avgDeal)}</div>
                )}
              </div>

              {/* Deal cards */}
              <div className="flex-1 p-2 flex flex-col gap-2 overflow-y-auto" style={{ maxHeight: '520px' }}>
                {stageDeals?.map((deal) => (
                  <div
                    key={deal?.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal?.id)}
                    onDragEnd={handleDragEnd}
                    className="rounded-lg p-3 cursor-grab active:cursor-grabbing transition-all duration-150"
                    style={{
                      background: draggingId === deal?.id ? stage?.bg : 'var(--color-background)',
                      border: '1px solid var(--color-border)',
                      opacity: draggingId === deal?.id ? 0.5 : 1,
                      boxShadow: draggingId === deal?.id ? 'var(--shadow-md)' : 'none',
                    }}
                  >
                    <div className="font-caption font-semibold text-xs mb-1 leading-tight" style={{ color: 'var(--color-foreground)' }}>
                      {deal?.name}
                    </div>
                    <div className="flex items-center justify-end gap-1 mb-1">
                      <button
                        type="button"
                        className="flex items-center justify-center rounded-md"
                        style={{ width: 22, height: 22, color: 'var(--color-text-secondary)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditDeal?.({
                            id: deal?.id,
                            name: deal?.name,
                            company: deal?.company,
                            value: deal?.value,
                            probability: deal?.probability,
                            stage: Number(deal?.stage ?? 1),
                            rep: deal?.rep || 'Unassigned',
                            closeDate: deal?.closeDate,
                            source: deal?.notes || '—',
                          });
                        }}
                        title="Edit deal"
                      >
                        <Icon name="Edit2" size={12} color="currentColor" />
                      </button>
                      <button
                        type="button"
                        className="flex items-center justify-center rounded-md"
                        style={{ width: 22, height: 22, color: '#DC2626' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteDeal?.(deal?.id);
                        }}
                        title="Delete deal"
                      >
                        <Icon name="Trash2" size={12} color="currentColor" />
                      </button>
                    </div>
                    <div className="font-caption text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      {deal?.company}
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-caption font-bold text-sm" style={{ color: stage?.color }}>
                        {formatRupees(deal?.value)}
                      </span>
                      <span
                        className="font-caption text-xs px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: stage?.bg, color: stage?.color }}
                      >
                        {deal?.probability}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={10} color="var(--color-text-secondary)" />
                        <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {deal?.closeDate}
                        </span>
                      </div>
                      <div
                        className="flex items-center justify-center rounded-full text-xs font-caption font-bold"
                        style={{ width: 20, height: 20, background: stage?.bg, color: stage?.color, fontSize: '9px' }}
                      >
                        {deal?.rep?.split(' ')?.map((n) => n?.[0])?.join('')}
                      </div>
                    </div>
                    {/* Probability bar */}
                    <div className="mt-2 rounded-full overflow-hidden" style={{ height: 3, background: 'var(--color-border)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${deal?.probability}%`, background: stage?.color }}
                      />
                    </div>
                  </div>
                ))}
                {stageDeals?.length === 0 && (
                  <div
                    className="flex flex-col items-center justify-center py-6 rounded-lg border-2 border-dashed"
                    style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                  >
                    <Icon name="Plus" size={16} color="currentColor" />
                    <span className="font-caption text-xs mt-1">Drop here</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
